/**
 * SC-209: the pure half of picture-in-picture. No DOM, no LiveKit room, no browser APIs.
 *
 * Everything the floating window renders is decided here so it can be tested without opening one —
 * which matters more than usual, because a PiP window cannot be driven from jsdom at all.
 *
 * **Deviation from the plan, recorded deliberately (2026-09-20).** `implementation-plan.md` §2.1 types the
 * track fields as `TrackReferenceOrPlaceholder`. The code wins: `VideoTrack`'s `trackRef` prop is
 * `TrackReference` (`@livekit/components-react/dist/components/participant/VideoTrack.d.ts:6`), and a
 * placeholder would not be assignable. Nothing is lost — a placeholder means "this participant has no live
 * track", which `cameraTrack === undefined` already says, and the tile renders "Camera off" from that.
 */

import type { TrackReference } from '@livekit/components-react'

import { promoteSpeakersIntoViewport, sidebarPromotionSets } from '../utils/sidebarOrder'
import type { CameraOffPictureInfo } from '../cameraOffPicture'

export type PipMode = 'document' | 'video' | 'none'

/** D3: four camera tiles, the cap Google Meet also settled on. */
export const PIP_TILE_CAP = 4

/**
 * What a tile should be showing, decided once rather than inferred from whether a track object happens to
 * exist.
 *
 * This distinction is the whole point. LiveKit keeps handing out a `TrackReference` for a camera that has
 * been switched off — the publication stays, it is simply muted — so "there is a track" is not the same
 * question as "is anything arriving". Rendering the one for the other leaves the last frame frozen on screen,
 * which reads as a broken tile rather than as someone who turned their camera off.
 */
export type PipCameraState =
  /** Frames are arriving; show the video. */
  | 'live'
  /** Deliberately off — theirs or ours. Say so. */
  | 'off'
  /** Meant to be on, nothing to show yet: a subscription that has not landed. Say that instead. */
  | 'waiting'

export interface PipTile {
  participantId: string
  isLocal: boolean
  isSpeaking: boolean
  micEnabled: boolean
  cameraState: PipCameraState
  /** Set only when `cameraState` is `'live'`, so a track in hand always means a moving picture. */
  cameraTrack?: TrackReference
  /**
   * SC-158: the camera is on but held for its background - ours while the effect gets ready or a problem
   * keeps it down, theirs as they told us. Its track can still be `'live'` (`pauseUpstream` does not mute our
   * own publication) and the frames behind it are the real room, so nothing may show it uncovered.
   */
  cameraWaiting: boolean
  /**
   * SC-158, Safari only: our own live camera picture while a None-to-effect switch holds it, kept on this
   * device alone (`readLocalCameraHoldPreview`). The document window ignores it and covers the tile, as the
   * in-page tile does.
   */
  heldPreview?: TrackReference
  displayName: string
  /** SC-415. Display-only: lowering someone else's hand stays in the tab, with the owner controls. */
  handRaised: boolean
  /** SC-270: photo or initials shown while the camera is off. Built from the name when absent. */
  picture?: CameraOffPictureInfo
}

/**
 * Whether the tile shows video, and what it says when it does not.
 *
 * A remote camera is off when its publication is muted — which is exactly what `isCameraEnabled` reports, and
 * what the track list does *not* filter on. Our own camera is a local decision: the track outlives the
 * toggle, so the tile has to read the same flag the rest of the UI does.
 */
export function pipCameraState(input: {
  isLocal: boolean
  /** `videoDisplayState.videoEnabled` — what we asked our own camera to do. */
  localVideoEnabled: boolean
  /** `participant.isCameraEnabled` — false once they mute the publication. */
  remoteCameraEnabled: boolean
  /** `publication.isMuted`, read directly, because a stale participant object is worse than a duplicate check. */
  publicationMuted: boolean
  /** There is a subscribed, attachable track behind the publication. */
  hasTrack: boolean
}): PipCameraState {
  const wanted = input.isLocal ? input.localVideoEnabled : input.remoteCameraEnabled
  if (!wanted || input.publicationMuted) return 'off'
  return input.hasTrack ? 'live' : 'waiting'
}

/**
 * The shape of the shared thing, which is what decides where it sits — across the top when it is wider than
 * it is tall, down the left when it is taller. The window's own shape does not come into it: a portrait phone
 * share laid across the top of a wide window is two thin slivers of letterboxing.
 */
export type PipContentOrientation = 'landscape' | 'portrait'

export interface PipShareContent {
  kind: 'share'
  participantId: string
  trackRef: TrackReference
  sharerName: string
  orientation: PipContentOrientation
}

export interface PipWhiteboardContent {
  kind: 'whiteboard'
  whiteboardId: string
  url: string
  participantId: string
  /** Whoever is on it — the window has no participant list to look a name up in. */
  participantName: string
  orientation: PipContentOrientation
}

export type PipContent = PipShareContent | PipWhiteboardContent | null

export interface PipModel {
  /** At most `PIP_TILE_CAP`, local always first. */
  tiles: PipTile[]
  /** People in proximity who did not get a tile. Local always has one, so it is never counted here. */
  overflow: number
  /**
   * Safari only: every remote present who did not get a tile (the cap, or a promotion that reordered the
   * slots), held or not. `keptPlace` still finds them, so the person the window is on does not lose their
   * place, before, during or after a hold, when someone else speaks. Never rendered and never counted: the
   * document window ignores it, and `PipHost` leaves it out there so that window's four slots are
   * untouched.
   */
  keep?: PipTile[]
  /** People in proximity including you — the number in the window header. */
  proximityCount: number
  content: PipContent
  roomName: string
  controls: {
    audioEnabled: boolean
    videoEnabled: boolean
    isAutoMuted: boolean
    autoMuteReason: string
    toggleAudio(): void
    toggleVideo(): void
    /** SC-415: present exactly when the call bar shows its raise-hand button, and the same toggle. */
    raiseHand?: {
      raised: boolean
      toggle(): void
    }
  }
}

/** What the main window is showing right now — the `maximized*` fields of `VideoDisplayState`. */
export interface VisibleContent {
  share: PipShareContent | null
  whiteboard: PipWhiteboardContent | null
}

/** Everything live, whether or not the main window has it on screen. */
export interface LiveContent {
  shares: PipShareContent[]
  whiteboards: PipWhiteboardContent[]
}

/**
 * D2: the shared cell holds whatever was visible when the window opened, until that source ends.
 *
 * The hold is the whole rule, and it is deliberate. Someone pops the window out to watch a demo; a colleague
 * then opens a whiteboard in the tab behind them. Following the tab would take the demo away mid-sentence,
 * so it does not — the cell keeps the demo until the demo stops, and only then looks for something else.
 *
 * `prev` is `null` whenever the window is closed, which is what makes "whatever was visible when it opened"
 * fall out of this function instead of needing a clock. While closed the model mirrors the tab, so the first
 * resolve after the click *is* that mirror, and every resolve after it holds.
 *
 * The held source is re-read out of `live` rather than passed straight back, so a track reference LiveKit has
 * rebuilt — or a share that has changed shape — travels through without the hold being broken.
 */
export function resolvePipContent(
  prev: PipContent,
  visible: VisibleContent,
  live: LiveContent,
): PipContent {
  const held = prev === null ? null : stillLive(prev, live)
  if (held) return held
  return visible.share ?? visible.whiteboard ?? live.shares[0] ?? live.whiteboards[0] ?? null
}

/** The same source as it stands now, or `null` once it has ended. Matched by identity, never by object. */
function stillLive(prev: PipShareContent | PipWhiteboardContent, live: LiveContent): PipContent {
  if (prev.kind === 'share') {
    return live.shares.find((share) => share.participantId === prev.participantId) ?? null
  }
  return live.whiteboards.find((board) => board.whiteboardId === prev.whiteboardId) ?? null
}

/**
 * Which way round a shared thing is.
 *
 * Landscape when nothing is known, because a shared screen nearly always is one and because being wrong
 * costs letterboxing rather than a broken layout. A whiteboard has no shape of its own — WBO's canvas is
 * whatever the frame is — so it takes the same default and never asks.
 */
export function contentOrientation(
  dimensions?: { width?: number; height?: number } | null,
): PipContentOrientation {
  const width = dimensions?.width ?? 0
  const height = dimensions?.height ?? 0
  if (width <= 0 || height <= 0) return 'landscape'
  return height > width ? 'portrait' : 'landscape'
}

/** Every screen share currently published to this room, in the order LiveKit hands them over. */
export function liveShares(
  tracks: readonly TrackReference[],
  displayNameFor: (participantId: string) => string,
): PipShareContent[] {
  return tracks.map((trackRef) => ({
    kind: 'share' as const,
    participantId: trackRef.participant.identity,
    trackRef,
    sharerName: displayNameFor(trackRef.participant.identity),
    orientation: contentOrientation(trackRef.publication?.dimensions),
  }))
}

/**
 * Every whiteboard the floating window is allowed to show.
 *
 * Two kinds qualify, and the difference matters. A board scoped to this virtual room is shared work —
 * anyone here may end up looking at it, so it is offered even when the tab has nothing open. A board with no
 * virtual room is a public one out on the map, which somebody across the office could be doodling on; that
 * only reaches the window once the tab has opened it.
 *
 * **`everVisible` is what makes D2's hold survive the tab.** Once a public board has been on screen in this
 * session it keeps qualifying for as long as people are still on it, so minimising it in the tab does not
 * empty a window that is holding it — minimising is not the source ending. Without that set, a public board
 * would drop out of this list the instant `visible` went null, and the held cell would blank.
 *
 * **Neither `everVisible` nor `visible` can put a board from another virtual room on this list.** A
 * room-scoped board qualifies only while you are in that room, exactly as its users only get a sidebar badge
 * there and it only auto-opens there. Having opened it inside a room you have since left — possibly a locked
 * one — is not a reason to keep holding it, and because the hold re-checks this list, leaving the room ends
 * the hold.
 *
 * What this list does not stop is the mirror: `resolvePipContent` falls back to whatever the tab has
 * maximised, unfiltered, so if the tab is still showing that board the window shows it too, until the tab
 * closes it. That only happens when the server moves you while the modal is open (a follower swept along by a
 * leader's teleport), and the window then shows nothing the tab is not already showing. Filtering the
 * fallback as well would make the window drop a board the tab still shows.
 */
export function liveWhiteboards(input: {
  /** `state.whiteboard.activeWhiteboardUsers` — whiteboardId → the session ids on it. */
  activeWhiteboardUsers: Record<string, string[]>
  /** `state.whiteboard.whiteboardVrIds` — absent means a public board. */
  whiteboardVrIds: Record<string, string>
  whiteboardUrls: Record<string, string>
  localVrId: string | null
  /** The board the tab has maximised. Offered only when it is public or belongs to this room. */
  visible: PipWhiteboardContent | null
  /** Every board this tab has had open during the session — see the note above. */
  everVisible: ReadonlySet<string>
  displayNameFor: (participantId: string) => string
}): PipWhiteboardContent[] {
  const boards: PipWhiteboardContent[] = []

  // A room-scoped board is offered only in its own room; a public one needs the tab to have shown it.
  const qualifies = (whiteboardId: string, rescued: boolean) => {
    const vrId = input.whiteboardVrIds[whiteboardId]
    return vrId ? vrId === input.localVrId : rescued
  }

  for (const [whiteboardId, users] of Object.entries(input.activeWhiteboardUsers)) {
    if (users.length === 0) continue
    const url = input.whiteboardUrls[whiteboardId]
    if (!url) continue

    if (!qualifies(whiteboardId, input.everVisible.has(whiteboardId))) continue

    boards.push({
      kind: 'whiteboard',
      whiteboardId,
      url,
      participantId: users[0],
      participantName: input.displayNameFor(users[0]),
      orientation: 'landscape',
    })
  }

  const visible = input.visible
  if (
    visible &&
    qualifies(visible.whiteboardId, true) &&
    !boards.some((board) => board.whiteboardId === visible.whiteboardId)
  ) {
    boards.push(visible)
  }

  return boards
}

/**
 * R3/R7: who gets one of the four tiles.
 *
 * You are always first, because a window that can lose your own tile reads as a bug. Anyone sharing is
 * pinned next, since they are the reason the window is open, and then anyone with a raised hand (SC-415),
 * since a hand nobody can see is a question nobody answers. Whoever is speaking is then pulled into the
 * remaining slots. Pins and promotions come from the sidebar's own helpers (`sidebarPromotionSets`,
 * `promoteSpeakersIntoViewport`), so the window and the sidebar never disagree about who must stay visible.
 * The one difference is deliberate: the sidebar puts sharers ahead of you, and this keeps you first.
 */
export function selectPipTiles(input: {
  order: string[]
  speakingIds: Set<string>
  localId: string
  sharerIds: Set<string>
  handRaisedIds?: ReadonlySet<string>
  cap?: number
}): { tileIds: string[]; overflow: number } {
  const capacity = input.cap ?? PIP_TILE_CAP

  const { pinnedIds, promoteIds } = sidebarPromotionSets(
    input.order.map((id) => ({ identity: id, isLocal: id === input.localId })),
    input.sharerIds,
    input.speakingIds,
    input.handRaisedIds
  )

  const rank = (id: string) => (id === input.localId ? 0 : input.sharerIds.has(id) ? 1 : 2)
  // `sort` is stable, so within a rank the caller's order still decides.
  const pinned = input.order.filter((id) => pinnedIds.has(id)).sort((a, b) => rank(a) - rank(b))
  const base = [...pinned, ...input.order.filter((id) => !pinnedIds.has(id))]

  const promoted = promoteSpeakersIntoViewport(base, promoteIds, capacity, pinnedIds)
  const tileIds = promoted.slice(0, Math.max(0, capacity))

  // Local is always inside `tileIds` when present, so nobody counted here is you.
  return { tileIds, overflow: Math.max(0, input.order.length - tileIds.length) }
}

/** How the last tile fills the cells an odd number of them would otherwise leave empty. */
export type PipLastTileSpan = 'none' | 'columns' | 'rows'

export interface PipLayout {
  columns: string
  rows: string
  /** `grid-area` for the shared cell, or null when there is nothing shared. */
  contentArea: string | null
  /** How many tiles this arrangement holds — never more than it was offered, never more than the cap. */
  tileCount: number
  lastTileSpan: PipLastTileSpan
}

export interface PipLayoutInput {
  width: number
  height: number
  /** How many tiles there are to place. Capped here, so callers do not have to remember to. */
  tileCount: number
  /** `null` when nothing is shared. */
  content: PipContentOrientation | null
}

/**
 * R4/R9: the grid, sized to what is actually in it.
 *
 * The user drags this window and we never set its size, so this runs on every `resize` and has to give a
 * sensible answer at any dimension the browser allows. Two rules shape it:
 *
 * **Never reserve a cell for someone who is not there.** Two people in the room get two tiles filling the
 * window, not two tiles and two holes.
 *
 * **A shared screen takes the space of two tiles**, and sits across the top when it is landscape or down the
 * left when it is portrait — decided by the shape of the share, not the shape of the window. Four tiles
 * remain the cap either way, so a window with something shared holds five things in total.
 *
 * `height` decides one thing only: which way a pair of tiles is stacked when nothing is shared.
 */
export function pipLayout(input: PipLayoutInput): PipLayout {
  const tileCount = Math.max(0, Math.min(input.tileCount, PIP_TILE_CAP))

  if (input.content === null) return tilesOnly(tileCount, input.width >= input.height)
  if (input.content === 'landscape') return shareAcrossTheTop(tileCount)
  return shareDownTheSide(tileCount)
}

const FR = (n: number) => (n <= 1 ? '1fr' : `repeat(${n}, 1fr)`)

function tilesOnly(tileCount: number, windowIsLandscape: boolean): PipLayout {
  const base = { contentArea: null, tileCount, lastTileSpan: 'none' as PipLastTileSpan }

  if (tileCount <= 1) return { ...base, columns: '1fr', rows: '1fr' }

  if (tileCount === 2) {
    // A pair goes the long way round: side by side in a wide window, stacked in a tall one.
    return windowIsLandscape
      ? { ...base, columns: FR(2), rows: '1fr' }
      : { ...base, columns: '1fr', rows: FR(2) }
  }

  // Three or four: a 2×2 grid, with the third tile widened so three people never leave a hole.
  return { ...base, columns: FR(2), rows: FR(2), lastTileSpan: tileCount === 3 ? 'columns' : 'none' }
}

/** Two columns; the share owns row 1 (two cells), the tiles flow underneath. */
function shareAcrossTheTop(tileCount: number): PipLayout {
  const tileRows = Math.ceil(tileCount / 2)
  return {
    columns: FR(2),
    rows: FR(1 + tileRows),
    contentArea: '1 / 1 / 2 / 3',
    tileCount,
    lastTileSpan: tileCount % 2 === 1 ? 'columns' : 'none',
  }
}

/** Two rows; the share owns column 1 (two cells), the tiles flow to its right. */
function shareDownTheSide(tileCount: number): PipLayout {
  const tileColumns = Math.ceil(tileCount / 2)
  return {
    columns: FR(1 + tileColumns),
    rows: FR(2),
    contentArea: '1 / 1 / 3 / 2',
    tileCount,
    // A single tile beside the share has a whole column to itself, so it grows downwards rather than across.
    lastTileSpan: tileCount === 1 ? 'rows' : tileCount % 2 === 1 ? 'columns' : 'none',
  }
}

/**
 * The `document.title` of the picture-in-picture window.
 *
 * **Chrome will not show this, and that is by design rather than a bug to chase.** The Document
 * Picture-in-Picture specification requires the user agent to make the controlling origin clear at all times
 * and suggests a title bar for it; Chrome renders the origin — `localhost:5173` in development — and a page
 * cannot replace it. That is an anti-spoofing measure for a window that floats above everything else.
 * Checked against the spec and against Chrome on 2026-09-20, after assuming otherwise.
 *
 * It is still set, because a document title is the right thing for a document to have: other engines are
 * free to use it, and it is what window managers, screen readers and session restore reach for. What the
 * user actually reads is `PipRoot`'s own header row, which is why that row exists.
 */
export function pipWindowTitle(input: {
  /** The tab's own name, read from `document.title`. */
  appName: string
  roomName: string
  proximityCount: number
}): string {
  const named = [input.appName.trim(), input.roomName.trim()].filter(Boolean)
  const title = named.join(' - ')
  if (!title) return 'Picture-in-picture'
  return input.proximityCount > 0 ? `${title} · ${input.proximityCount}` : title
}

/**
 * R15: whether the floating window should be holding a screen wake lock right now.
 *
 * Open is the whole condition — content or not. Someone watching four faces while they read something else
 * has exactly the same problem as someone watching a shared screen: the machine has no idea they are in a
 * meeting, and the display sleeps on them.
 *
 * Measured 2026-09-20. On Chromium this works, because the picture-in-picture document counts as visible in
 * its own right and can hold a lock the opener cannot (a lock dies the moment its document is hidden, which
 * is precisely when someone tabs away and starts needing the window). On Firefox the same document reports
 * itself hidden while its window is plainly on screen, so the lock is refused. Nothing here branches on
 * that: the renderer asks, and a refusal is a non-event.
 */
export function shouldHoldPipWakeLock(input: { open: boolean; supported: boolean }): boolean {
  return input.open && input.supported
}

/**
 * SC-476: what Safari's window shows next. Not always a track: while a remote's camera is held for its
 * background the window keeps that person's place and shows a neutral picture, so it never swaps to someone
 * else and never has an empty source (which is what makes Safari close the window).
 */
export type SafariPick =
  | {
      kind: 'track'
      trackRef: TrackReference
      /** The remote whose camera this is. Never set for a share or for our own camera. */
      participantId?: string
    }
  | {
      kind: 'waiting'
      /** Whose place the window is keeping. For our own held camera there is no place to keep. */
      participantId: string
      /**
       * Not yet known to be a hold: their camera went off and no "waiting" message has come. See
       * `pickSafariPicture`.
       */
      settling: boolean
    }

/** What the open window shows now, so a hold can keep the place it was showing. */
export interface SafariOpenWindow {
  shown: SafariPick
  /** The grace for a camera that went off with no word of a hold has run out. */
  offSettled?: boolean
}

/**
 * R11: the one thing Safari can show.
 *
 * Safari has only the old single-video API — one `<video>` element, no chrome, no tiles, no buttons — so the
 * whole model has to collapse to a single track. This is that collapse, and the order is a judgement about
 * what someone who has tabbed away most needs to keep seeing.
 *
 * A shared screen wins outright: it is the reason anybody pops the call out. Then whoever is both speaking
 * and on camera, because a silent face is worth less than the person talking. Then any remote camera at all.
 * Only then your own, which is better than a black rectangle but is the least useful thing in the list — you
 * know what you look like.
 *
 * A whiteboard is deliberately absent: it is a cross-origin iframe, and an iframe cannot be put inside a
 * `<video>`. Safari falls through to the speaker, and the vault doc says so out loud.
 */
export function pickSafariTrack(model: PipModel): TrackReference | undefined {
  const pick = pickSafariPicture(model)
  return pick?.kind === 'track' ? pick.trackRef : undefined
}

/**
 * {@link pickSafariTrack}, plus the one thing it cannot know: who an already open window is showing.
 *
 * Without `open` this is the fresh pick, which is what a closed window follows. With it, SC-476's rule for
 * the person the window is on:
 *
 * - **Held for their background** (`cameraWaiting`): they keep the window, as `waiting`. A held camera is
 *   never shown (SC-158: its last frame is the room the effect hides), and nobody else takes the window
 *   either, because a source change is what made Safari move the window and close it. The pin has no
 *   timeout on purpose: a hold can last up to 30 s and the receiver cannot tell it from a background that
 *   keeps failing, so a cap would drop legitimate holds. It ends when that person acts (their camera
 *   comes back or goes off), leaves, or the window is closed.
 * - **Back, after a hold:** their picture again, whoever is speaking, since the place was kept for them.
 *   That holds even where a promotion has since pushed them out of the tiles, because `keep` carries every
 *   remote outside them.
 * - **Gone off with no word of a hold yet:** `waiting` and `settling` until `offSettled`. A hold reaches us
 *   as two messages that travel apart (the camera's mute on the signal connection, `waiting` on the data
 *   channel), so the mute can come first and look exactly like a camera turned off. Past the grace it is
 *   treated as off, and the usual choice of whom to show applies.
 *
 * A share still wins over all of it: starting one is a choice, not a side effect of someone's background.
 */
export function pickSafariPicture(
  model: PipModel,
  open?: SafariOpenWindow
): SafariPick | undefined {
  if (model.content?.kind === 'share') return { kind: 'track', trackRef: model.content.trackRef }

  const kept = open ? keptPlace(model, open) : undefined
  if (kept) return kept

  // `cameraTrack` is only ever set when frames are actually arriving (see `pipCameraState`), so everything
  // below is a moving picture rather than a publication that might be muted. SC-158: a camera held for its
  // background is never one of them. This window is one bare video with no cover to put over it, and what
  // a held camera carries (or last carried) is the room the effect exists to hide.
  const live = model.tiles.filter((tile) => tile.cameraTrack && !tile.cameraWaiting)
  const remote = live.filter((tile) => !tile.isLocal)

  const person = remote.find((tile) => tile.isSpeaking) ?? remote[0]
  if (person?.cameraTrack) {
    return { kind: 'track', trackRef: person.cameraTrack, participantId: person.participantId }
  }

  // The user's decision of 2026-10-08, no window pauses the video: our own camera, held for a switch from
  // no effect to one, is shown as the live picture we kept of it until the effect's first frame. Only ever
  // ours, and only where ours would be shown anyway.
  const ownHeld = model.tiles.find((tile) => tile.isLocal && tile.cameraWaiting)

  const trackRef = live.find((tile) => tile.isLocal)?.cameraTrack ?? ownHeld?.heldPreview
  if (trackRef) return { kind: 'track', trackRef }

  // Ours is on but held, with no picture kept of it (a camera that starts with an effect, say): an open
  // window says so, rather than that no camera is on. Nobody's place is kept by this, see `keptPlace`.
  return ownHeld
    ? { kind: 'waiting', participantId: ownHeld.participantId, settling: false }
    : undefined
}

function keptPlace(
  model: PipModel,
  { shown, offSettled }: SafariOpenWindow
): SafariPick | undefined {
  const participantId = shown.participantId
  if (!participantId) return undefined
  const isIt = (other: PipTile) => !other.isLocal && other.participantId === participantId
  const tile = model.tiles.find(isIt) ?? model.keep?.find(isIt)
  if (!tile) return undefined

  if (tile.cameraWaiting) return { kind: 'waiting', participantId, settling: false }
  if (tile.cameraTrack) {
    return shown.kind === 'waiting'
      ? { kind: 'track', trackRef: tile.cameraTrack, participantId }
      : undefined
  }
  // A hold that was confirmed (`shown` is a plain `waiting`) and has now ended with the camera still off is
  // a camera turned off, not one whose "waiting" message may be late: the grace has already been waited out
  // by the store, and a second 1.5 s here would put the caption up twice.
  const confirmedHold = shown.kind === 'waiting' && !shown.settling
  return offSettled || confirmedHold
    ? undefined
    : { kind: 'waiting', participantId, settling: true }
}
